import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaMobileAlt,
  FaShieldAlt,
  FaDatabase,
  FaUserCog,
  FaQrcode,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../css/MechanicFindersPage.css';

import screenLanding from '../assets/oz/landing.png';
import screenLogin from '../assets/oz/login.png';
import screenUserDashboard from '../assets/oz/user-dashboard.png';
import screenServices from '../assets/oz/services.png';
import screenAdminDashboard from '../assets/oz/admin-dashboard.png';
import screenAdminServices from '../assets/oz/admin-services.png';
import mechanicFinderQr from '../assets/promo/mechanic-finder-qr.png';
import appStoreBadge from '../assets/promo/app-store-badge.png';

const APP_STORE_LINK = 'https://apps.apple.com/ca/app/mechanicfinders/id6759413308';

const features = [
  {
    icon: <FaMobileAlt />,
    title: 'Mobile-first product',
    text: 'A polished customer-facing mobile app built for modern service booking.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure authentication',
    text: 'JWT-based auth, protected flows, and structured access control.'
  },
  {
    icon: <FaDatabase />,
    title: 'FastAPI + MongoDB',
    text: 'Scalable APIs, clean data structure, and real service operations.'
  },
  {
    icon: <FaUserCog />,
    title: 'Admin workflows',
    text: 'Bookings, customers, services, vehicles, and management tools in one system.'
  }
];

const screens = [
  {
    image: screenLanding,
    label: 'Landing',
    title: 'Customer-facing landing flow'
  },
  {
    image: screenLogin,
    label: 'Login',
    title: 'Secure sign-in experience'
  },
  {
    image: screenUserDashboard,
    label: 'User',
    title: 'Customer dashboard and quick actions'
  },
  {
    image: screenServices,
    label: 'Services',
    title: 'Service cards and booking flow'
  },
  {
    image: screenAdminDashboard,
    label: 'Admin',
    title: 'Admin dashboard overview'
  },
  {
    image: screenAdminServices,
    label: 'Manager',
    title: 'Service management panel'
  }
];

export default function MechanicFindersPage() {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % screens.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + screens.length) % screens.length);
  };

  const activeScreen = screens[activeIndex];

  return (
    <div className="mf-page">
      <div className="mf-bg-orb orb-left" />
      <div className="mf-bg-orb orb-right" />
      <div className="mf-grid-overlay" />
      <div className="mf-watermark">MECHANIC FINDERS</div>

      <div className="mf-shell">
        <motion.div
          className="mf-topbar"
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <Link to="/" className="mf-back-link">
            <FaArrowLeft />
            Back to Home
          </Link>

          <a
            href={APP_STORE_LINK}
            target="_blank"
            rel="noreferrer"
            className="mf-store-link-top"
          >
            App Store
            <FaExternalLinkAlt />
          </a>
        </motion.div>

        <div className="mf-hero-layout">
          <motion.div
            className="mf-left"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="mf-tag">Case Study / Product Page</span>
            <h1>Mechanic Finders App</h1>
            <p className="mf-lead">
              Mechanic Finders is a modern automotive service platform designed
              for customers and admin operators. It brings together bookings,
              vehicle records, service flows, secure authentication, and real
              business logic in one premium mobile product.
            </p>

            <div className="mf-feature-grid">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="mf-feature-card"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + index * 0.06, duration: 0.35 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="mf-feature-icon">{feature.icon}</div>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mf-stat-row">
              <div className="mf-stat-box">
                <strong>2</strong>
                <span>Customer + Admin</span>
              </div>
              <div className="mf-stat-box">
                <strong>6+</strong>
                <span>Core Screens</span>
              </div>
              <div className="mf-stat-box">
                <strong>JWT</strong>
                <span>Secure Access</span>
              </div>
            </div>

            <div className="mf-cta-row">
              <a
                href={APP_STORE_LINK}
                target="_blank"
                rel="noreferrer"
                className="mf-btn mf-btn-primary"
              >
                Open on App Store
              </a>

              <Link to="/#projects" className="mf-btn mf-btn-secondary">
                More Projects
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="mf-right"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75 }}
          >
            <div className="mf-screen-panel">
              <div className="mf-screen-topbar">
                <div className="mf-screen-meta">
                  <span className="mf-screen-label">{activeScreen.label}</span>
                  <h3>{activeScreen.title}</h3>
                </div>

                <div className="mf-screen-controls">
                  <button type="button" onClick={prevSlide} aria-label="Previous screen">
                    <FaChevronLeft />
                  </button>
                  <button type="button" onClick={nextSlide} aria-label="Next screen">
                    <FaChevronRight />
                  </button>
                </div>
              </div>

              <div className="mf-phone-stage">
                <motion.div
                  className="mf-phone-card mf-phone-back left"
                  animate={{ y: [0, -8, 0], rotate: [-8, -10, -8] }}
                  transition={{ repeat: Infinity, duration: 5.5, ease: 'easeInOut' }}
                >
                  <img
                    src={screens[(activeIndex + 1) % screens.length].image}
                    alt="Mechanic Finders preview background left"
                  />
                </motion.div>

                <motion.div
                  className="mf-phone-card mf-phone-back right"
                  animate={{ y: [0, -10, 0], rotate: [8, 10, 8] }}
                  transition={{ repeat: Infinity, duration: 5.9, ease: 'easeInOut' }}
                >
                  <img
                    src={screens[(activeIndex + 2) % screens.length].image}
                    alt="Mechanic Finders preview background right"
                  />
                </motion.div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    className="mf-phone-card mf-phone-main"
                    initial={{ opacity: 0, y: 30, scale: 0.94, rotate: -2 }}
                    animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, y: -18, scale: 0.96, rotate: 2 }}
                    transition={{ duration: 0.45 }}
                  >
                    <div className="mf-phone-notch" />
                    <img
                      src={activeScreen.image}
                      alt={activeScreen.title}
                      className="mf-phone-image"
                    />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mf-screen-thumbs">
                {screens.map((screen, index) => (
                  <button
                    key={screen.label}
                    type="button"
                    className={`mf-thumb ${activeIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show ${screen.label}`}
                  >
                    <img src={screen.image} alt={screen.label} />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="mf-qr-section"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="mf-qr-copy">
            <span className="mf-qr-chip">
              <FaQrcode />
              App Store QR
            </span>
            <h2>Scan to download Mechanic Finders</h2>
            <p>
              Use your iPhone camera to open the App Store instantly and access
              the app directly.
            </p>

            <a
              href={APP_STORE_LINK}
              target="_blank"
              rel="noreferrer"
              className="mf-store-badge-link"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                className="mf-store-badge"
              />
            </a>
          </div>

          <a
            href={APP_STORE_LINK}
            target="_blank"
            rel="noreferrer"
            className="mf-qr-card"
            aria-label="Open Mechanic Finders on the App Store"
          >
            <img
              src={mechanicFinderQr}
              alt="Mechanic Finders App Store QR"
              className="mf-qr-image"
            />
          </a>
        </motion.div>
      </div>
    </div>
  );
}